<script setup lang="ts">
import { number_from_query, str_from_query } from "@/api";
import type { User } from "@/api/user";
import PaginationTemplate from "@/components/PaginationTemplate.vue";
import UserCreate from "@/components/User/UserCreate.vue";
import UserEdit from "@/components/User/UserEdit.vue";
import { useDeleteUserMutation, useUsersQuery } from "@/composables/useUser";
import { router } from "@/router/router";
import dayjs from "dayjs";
import { computed, reactive, ref, watch } from "vue";
import { toast } from "vue3-toastify";

const keyword = computed(() => str_from_query("keyword") || "");
const debounceTimer = ref<ReturnType<typeof setTimeout> | null>(null);
const isCreateUserOpen = ref(false);
const isEditUserOpen = ref(false);
const selectedUser = ref<User | null>(null);

const filters = reactive({
  keyword: keyword.value,
  page: number_from_query("page", 1),
  size: number_from_query("size", 10),
  status: [true, false],
});

// Watch URL query params and update filters
watch(
  () =>
    [
      str_from_query("keyword"),
      number_from_query("page", 1),
      number_from_query("size", 10),
    ] as const,
  ([newKeyword, newPage, newSize]) => {
    filters.keyword = newKeyword ?? "";
    filters.page = newPage;
    filters.size = newSize;
  },
  { immediate: true },
);

const { users, totalFiltered, isLoading, isError, error } =
  useUsersQuery(filters);

const totalPages = computed(() => {
  return Math.ceil(totalFiltered.value / filters.size);
});

const handleSearch = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const newKeyword = target.value;

  if (debounceTimer.value) {
    clearTimeout(debounceTimer.value);
  }

  debounceTimer.value = setTimeout(() => {
    router.push({
      query: {
        ...router.currentRoute.value.query,
        keyword: newKeyword || undefined,
        page: undefined, // Reset to page 1 when searching
      },
    });
  }, 500);
};

const { mutate: deleteUser } = useDeleteUserMutation();

const handleEdit = (user: User) => {
  selectedUser.value = user;
  isEditUserOpen.value = true;
};

const handleDelete = (user: User) => {
  if (confirm(`Bạn có chắc chắn muốn xóa người dùng "${user.username}"?`)) {
    deleteUser(user.id, {
      onSuccess: () => {
        toast.success("Xóa người dùng thành công");
      },
      onError: () => {
        toast.error("Lỗi khi xóa người dùng");
      },
    });
  }
};
</script>

<template>
  <div>
    <div class="mb-4 flex flex-wrap items-center justify-between gap-4">
      <label class="input input-bordered w-full max-w-xs">
        <i class="fa-solid fa-magnifying-glass text-base-content/80"></i>
        <input
          type="text"
          placeholder="Tìm kiếm theo tên người dùng..."
          :value="keyword"
          @input="handleSearch"
        />
      </label>
      <button class="btn btn-primary" @click="isCreateUserOpen = true">
        <i class="fa-solid fa-plus"></i> Tạo người dùng
      </button>
    </div>

    <UserCreate v-model:open="isCreateUserOpen" />
    <UserEdit v-model:open="isEditUserOpen" :user="selectedUser" />
    <div class="overflow-x-auto">
      <table class="bg-base-200 table">
        <thead class="bg-base-100">
          <tr>
            <th class="text-center">Số thứ tự</th>
            <th>Tên người dùng</th>
            <th>User ID</th>
            <th>Trạng thái</th>
            <th>Ngày tạo</th>
            <th>Ngày cập nhật</th>
            <th>Thao tác</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="isLoading">
            <td colspan="7" class="text-center">Đang tải dữ liệu...</td>
          </tr>
          <tr v-else-if="isError">
            <td colspan="7" class="text-error text-center">
              Lỗi khi tải dữ liệu: {{ error?.message }}
            </td>
          </tr>
          <tr v-else-if="!users || users.length === 0">
            <td colspan="7" class="text-center">Không có người dùng nào.</td>
          </tr>
          <tr v-else v-for="(user, index) in users" :key="user.id">
            <td class="text-center">
              {{ (filters.page - 1) * filters.size + index + 1 }}
            </td>
            <td>
              <div class="flex items-center gap-3">
                <div class="avatar">
                  <div class="w-10 rounded-full shadow">
                    <img
                      :alt="user.username"
                      :src="`https://api.dicebear.com/9.x/identicon/svg?seed=${encodeURIComponent(user.avatar_seed || user.username)}&backgroundType=solid&backgroundColor=dfe3f5&rowColor=3d4272`"
                    />
                  </div>
                </div>
                <span>{{ user.username }}</span>
              </div>
            </td>
            <td class="font-mono text-sm select-all">{{ user.id }}</td>
            <td>
              <span
                :class="{
                  'badge badge-success': user.enable,
                  'badge badge-error': !user.enable,
                }"
              >
                {{ user.enable ? "Hoạt động" : "Không hoạt động" }}
              </span>
            </td>
            <td>
              {{
                user.created_at
                  ? dayjs(user.created_at).fromNow()
                  : "Không có thông tin"
              }}
            </td>
            <td>
              {{
                user.updated_at
                  ? dayjs(user.updated_at).fromNow()
                  : "Không có thông tin"
              }}
            </td>
            <td>
              <div class="flex flex-wrap gap-2">
                <button class="btn" @click="handleEdit(user)">
                  <i class="fas fa-edit text-base-content/95"></i>
                  Cập nhật
                </button>
                <button
                  class="btn btn-outline btn-error"
                  @click="handleDelete(user)"
                >
                  <i class="fas fa-trash opacity-90"></i>
                  Xóa
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-if="!isLoading && users && users.length > 0" class="mt-4">
      <PaginationTemplate :total_pages="totalPages" />
    </div>
  </div>
</template>
